import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export const GrowthComparisonChart = ({ className }) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const chartRef = useRef(null);

  // Months for X-axis
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Growth data - normalized index starting at 20
  // Without Avalon: 3-7% monthly growth (slow) - raised by ~10%
  const withoutAvalonData = [20, 22, 24.5, 27, 30, 33, 36.5, 40, 44, 48, 52.5, 57];
  
  // With Avalon: 11-24% monthly growth (updated from 20-35%)
  // Recalculated to achieve ~240% total gain with new percentages
  const withAvalonData = [20, 23, 28, 35, 44, 55, 68, 83, 100, 120, 142, 168];

  // Chart dimensions - enlarged by 40% total
  const chartWidth = 840;
  const chartHeight = 380;
  const paddingLeft = 65;
  const paddingRight = 45;
  const paddingTop = 35;
  const paddingBottom = 55;
  const graphWidth = chartWidth - paddingLeft - paddingRight;
  const graphHeight = chartHeight - paddingTop - paddingBottom;

  // Scale functions
  const maxY = 180;
  const minY = 0;
  const scaleX = (index) => paddingLeft + (index / 11) * graphWidth;
  const scaleY = (value) => paddingTop + graphHeight - ((value - minY) / (maxY - minY)) * graphHeight;

  // Generate smooth curve path using Catmull-Rom to Bezier conversion
  const generateSmoothPath = (data, progress = 1) => {
    const visiblePoints = Math.ceil(data.length * progress);
    const points = data.slice(0, visiblePoints).map((value, index) => ({
      x: scaleX(index),
      y: scaleY(value)
    }));

    if (points.length < 2) return '';

    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[Math.min(points.length - 1, i + 1)];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    return path;
  };

  // Generate area path for gradient fill
  const generateAreaPath = (data, progress = 1) => {
    const linePath = generateSmoothPath(data, progress);
    if (!linePath) return '';
    
    const visiblePoints = Math.ceil(data.length * progress);
    const lastX = scaleX(visiblePoints - 1);
    const firstX = scaleX(0);
    const bottomY = scaleY(minY);

    return `${linePath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
  };

  // Intersection observer for animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Animation effect
  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const duration = 2000; // 2 seconds

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      setAnimationProgress(easeOutCubic);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible]);

  // Y-axis labels
  const yLabels = [0, 45, 90, 135, 180];

  return (
    <div 
      ref={chartRef}
      className={cn(
        'relative overflow-hidden rounded-3xl',
        className
      )}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      
      {/* Subtle grid pattern - updated color */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3A3F48_1px,transparent_1px),linear-gradient(to_bottom,#3A3F48_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-30" />
      
      {/* Glow effects */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#00E394]/8 blur-3xl rounded-full" />
      <div className="absolute bottom-0 left-1/4 w-1/3 h-1/3 bg-[#00E394]/5 blur-2xl rounded-full" />

      {/* Content - reduced top padding */}
      <div className="relative px-6 py-6 md:px-10 md:py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {t.growthChart.title}
          </h3>
          <p className="text-slate-400 text-base md:text-lg">
            {t.growthChart.subtitle}
          </p>
        </div>

        {/* Chart Container */}
        <div className="relative flex justify-center">
          <svg 
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full max-w-[840px]"
            style={{ overflow: 'visible' }}
          >
            {/* Definitions for gradients and filters */}
            <defs>
              {/* Avalon green gradient for area - brighter */}
              <linearGradient id="avalonAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00E394" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#00E394" stopOpacity="0" />
              </linearGradient>
              
              {/* Avalon green line gradient - brighter range */}
              <linearGradient id="avalonLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#00E394" />
                <stop offset="50%" stopColor="#00FFAB" />
                <stop offset="100%" stopColor="#00E394" />
              </linearGradient>
              
              {/* Enhanced glow filter for Avalon line - even stronger */}
              <filter id="glowFilter" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="5" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* Drop shadow for arrow */}
              <filter id="arrowShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#00C67A" floodOpacity="0.5" />
              </filter>
            </defs>

            {/* Grid lines - updated color #3A3F48 */}
            {yLabels.map((label, i) => (
              <g key={i}>
                <line
                  x1={paddingLeft}
                  y1={scaleY(label)}
                  x2={chartWidth - paddingRight}
                  y2={scaleY(label)}
                  stroke="#3A3F48"
                  strokeDasharray="4,4"
                  opacity="0.5"
                />
                <text
                  x={paddingLeft - 14}
                  y={scaleY(label) + 6}
                  fill="rgba(255,255,255,0.55)"
                  fontSize="15"
                  fontWeight="600"
                  textAnchor="end"
                  fontFamily="Inter, sans-serif"
                >
                  {label}
                </text>
              </g>
            ))}

            {/* X-axis labels - increased font size */}
            {months.map((month, i) => (
              <text
                key={month}
                x={scaleX(i)}
                y={chartHeight - 14}
                fill="rgba(255,255,255,0.65)"
                fontSize="16"
                fontWeight="600"
                textAnchor="middle"
                fontFamily="Inter, sans-serif"
              >
                {month}
              </text>
            ))}

            {/* Without Avalon area (very subtle) */}
            <path
              d={generateAreaPath(withoutAvalonData, animationProgress)}
              fill="rgba(139, 143, 151, 0.08)"
            />

            {/* Without Avalon line - lighter color #8B8F97, thicker */}
            <path
              d={generateSmoothPath(withoutAvalonData, animationProgress)}
              fill="none"
              stroke="#8B8F97"
              strokeWidth="4"
              strokeLinecap="round"
              opacity="0.75"
            />

            {/* With Avalon area (gradient) */}
            <path
              d={generateAreaPath(withAvalonData, animationProgress)}
              fill="url(#avalonAreaGradient)"
            />

            {/* With Avalon line (glowing) - brighter green, much thicker */}
            <path
              d={generateSmoothPath(withAvalonData, animationProgress)}
              fill="none"
              stroke="url(#avalonLineGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              filter="url(#glowFilter)"
            />

            {/* Data points removed - no dots displayed */}

            {/* Growth Arrow and Badge - appears after animation */}
            {animationProgress > 0.8 && (
              <g 
                style={{ 
                  opacity: (animationProgress - 0.8) * 5,
                  transform: `translateY(${(1 - (animationProgress - 0.8) * 5) * 20}px)`
                }}
              >
                {/* Arrow background - updated color #00C67A */}
                <rect
                  x={scaleX(8) - 10}
                  y={scaleY(135) - 50}
                  width="160"
                  height="44"
                  rx="22"
                  fill="#00C67A"
                  filter="url(#arrowShadow)"
                />
                
                {/* Arrow icon - white */}
                <g transform={`translate(${scaleX(8)}, ${scaleY(135) - 28})`}>
                  <path
                    d="M0 8 L8 0 L16 8 M8 0 L8 16"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </g>
                
                {/* Badge text - white, larger */}
                <text
                  x={scaleX(8) + 28}
                  y={scaleY(135) - 32}
                  fill="white"
                  fontSize="15"
                  fontWeight="700"
                  fontFamily="Inter, sans-serif"
                >
                  {t.growthChart.fasterGrowth}
                </text>
                <text
                  x={scaleX(8) + 28}
                  y={scaleY(135) - 17}
                  fill="rgba(255,255,255,0.95)"
                  fontSize="13"
                  fontWeight="600"
                  fontFamily="Inter, sans-serif"
                >
                  {t.growthChart.growthWithAvalon}
                </text>
              </g>
            )}
          </svg>
        </div>

        {/* Legend - updated values with larger text */}
        <div className="flex flex-wrap items-center justify-center gap-8 mt-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-1.5 rounded-full" style={{ backgroundColor: '#8B8F97', opacity: 0.7 }} />
            <span className="text-slate-400 text-base font-medium">{t.growthChart.withoutAvalon}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-1.5 rounded-full bg-gradient-to-r from-[#00E394] to-[#00FFAB] shadow-lg shadow-[#00E394]/40" />
            <span className="text-white text-base font-semibold">{t.growthChart.withAvalon}</span>
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-center text-slate-500 text-base mt-6 max-w-md mx-auto">
          {t.growthChart.description}
        </p>
      </div>
    </div>
  );
};
